/**
 * §28 — everything the outbox needs from its host, and nothing else.
 *
 * The queue core is deliberately free of React Native, the DOM and axios: a
 * waiter app on AsyncStorage, a dashboard on IndexedDB and a test on a `Map`
 * all drive the same code, so the rule that decides whether an order is lost
 * cannot differ between them. Each host supplies these three ports.
 */

/**
 * Durable key/value storage, async because the only honest options are.
 *
 * `@react-native-async-storage/async-storage` satisfies this as-is. A browser
 * host must supply an **IndexedDB**-backed implementation: `localStorage` is
 * synchronous, capped at a few megabytes, and its writes are not durable across
 * a crash mid-write, so it cannot hold a queue that money depends on. The port
 * is async precisely so IndexedDB fits without the core knowing. `getItem` must
 * answer `null` — never throw — for a key that was never written.
 */
export interface OfflineStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/** What the outbox hands the transport. `data` and `headers` are always set. */
export interface OutboxRequest {
  method: 'post' | 'patch' | 'delete';
  url: string;
  data: Record<string, unknown>;
  headers: Record<string, string>;
}

/**
 * The host's HTTP client, narrowed to the one call the outbox makes. It must
 * **reject** on a non-2xx with an axios-shaped error — `error.response.status`
 * and `error.response.data.message` — because that shape is what
 * {@link statusOf} and {@link failureMessage} read to decide between "retry
 * later" and "this will never land". A client that resolves on a 4xx would make
 * the outbox count a refusal as a delivery and drop the work.
 */
export type OutboxTransport = (request: OutboxRequest) => Promise<{ data: unknown }>;

/**
 * Whether it is worth touching the network at all. Answer `true` when unsure:
 * every host that got this wrong got it wrong the same way, reading a platform
 * that never reports `isInternetReachable` as "offline" so the app never
 * flushed at all. A wasted request costs a round trip; a false "offline" costs
 * the whole queue its chance to drain.
 */
export type ReachabilityProbe = () => Promise<boolean>;

/**
 * An in-memory {@link OfflineStorage}, for tests and for a host that genuinely
 * has nowhere durable to write. Nothing survives a reload — never ship it as a
 * real device's queue.
 */
export function memoryStorage(disk: Map<string, string> = new Map()): OfflineStorage {
  return {
    getItem: async (key) => disk.get(key) ?? null,
    setItem: async (key, value) => {
      disk.set(key, value);
    },
    removeItem: async (key) => {
      disk.delete(key);
    },
  };
}
